import { FC, useMemo } from 'react'
import { Outlet, redirect, useMatch } from 'react-router-dom'
import { useUser } from '@clerk/clerk-react'

// COMPONENTS
import { Column } from '@components/templates/column'
import GroupBanner from '@components/organisims/group-banner'

// ROUTES
import { AddPledgeIdRoute } from './[pledgeId]'

// TYPES
import type { RouteObject } from 'react-router-dom'
import { createAuthenticatedLoader, refreshClerkUser } from '@utils/loaders'
import { parseUserLimits } from '@schemas'
import { useGroupLoaderData } from '..'
import { getGroupStatus } from '@utils/temp-helpers'
import { isNonEmptyString } from '@utils/type-checkers'
import { priceFormatter } from '@utils/number-helper'

const Layout: FC<{}> = () => {
    const { isSignedIn, user } = useUser()
    const group = useGroupLoaderData()
    const { groupStatus } = getGroupStatus(group)

    const isAdd = useMatch({ path: '/group/:groupId/add/:pledgeId/addPM' })

    const { userPledges } = useMemo(() => [...(group.pledges || [])].reduce<{ userPledges: Pledge[] }>((prev, pledge) => {
        if (isSignedIn && isNonEmptyString(user.id)) {
            if (pledge.user?.id === user.id) return { ...prev, userPledges: [...prev.userPledges, pledge] };
            else return { ...prev }
        } else return prev
    }, { userPledges: [] }), [isSignedIn, user?.id, group.pledges])

    const isOrganizer = isSignedIn ? group.organizer?.id === user.id : false
    const isGroupMember = !!userPledges.length

    return (<>
        <Column.Content className='group-banner-section' style={{ padding: '0rem' }}>
            {!isAdd && <>
                <GroupBanner isOrganizer={isOrganizer} isGroupMember={isGroupMember} group={group} groupStatus={groupStatus} />
                <h4 className='text-center text-gray-500'>{priceFormatter.format(((group.collectedAmount || 0) - (group.spendAmount || 0)) / 100)} Remaining Funds</h4>
            </>}
        </Column.Content >
        <Column.Content className="group-edit-screen">
            <Outlet />
        </Column.Content>
    </>)
}

const AddRoute: RouteObject = {
    path: 'add',
    element: <Layout />,
    loader: createAuthenticatedLoader(async ({ params }, Clerk) => {
        await refreshClerkUser(Clerk)
        const userLimits = parseUserLimits(Clerk.user?.publicMetadata?.limits)
        if (!userLimits.addAdditionalFunds && isNonEmptyString(params.groupId)) {
            return redirect(`/group/${params.groupId}`)
        }

        return null
    }),
    children: [
        AddPledgeIdRoute,
    ]
}

export default AddRoute
